import {
  BitkitBadge,
  BitkitBadgeProps,
  BitkitIconComponent,
  BitkitPageSidebar,
  BitkitPageSidebarTitleProps,
} from '@bitrise/bitkit-v2';
import { Box, BoxProps } from '@chakra-ui/react/box';
import { Fragment, ReactNode, RefObject, useRef } from 'react';

import { PAGE_HEADER_STICKY_HEIGHT } from '@/components/page/PageHeader/StickyPageHeader';

import useIsFullSidebarVisible from './useIsFullSidebarVisible';
import useSidebarHeight from './useSidebarHeight';

export type PageLayoutSidebarLink = {
  pageName: string;
  icon?: BitkitIconComponent;
  itemProps: {
    disabled?: boolean;
    disabledTooltip?: string;
    href?: string;
    onClick?: () => void;
  };
  label: string;
  badge?: {
    label: string;
    variant: BitkitBadgeProps['variant'];
    colorVariant: BitkitBadgeProps['colorVariant'];
  };
  suffixIcon?: BitkitIconComponent;
};
type PageLayoutSidebarDivider = { divider: true; label?: string };
export type PageLayoutSidebarItem =
  | PageLayoutSidebarLink
  | PageLayoutSidebarDivider
  // A row the caller renders itself, for a list that is not navigation -- the release flow's
  // stages, which are `BitkitPageSidebar.ProgressItem` rather than `.Item`.
  | { custom: ReactNode; key: string };

export type PageLayoutSidebarProps = {
  clarityUnmask?: boolean;
  extraContent?: ReactNode;
  isMobile: boolean;
  isStickyHeader: boolean;
  items: PageLayoutSidebarItem[];
  footer?: ReactNode;
  platformShell: {
    headerHeight: number;
    navWidth: number;
  };
  selectedPage: string;
  pageHeaderRef: RefObject<HTMLDivElement>;
  /**
   * The sub-view's heading, with the back control the design system draws and places. Above `Body`
   * rather than in `items`, which is where the sidebar's own header lives.
   */
  title?: BitkitPageSidebarTitleProps;
  width: '16rem' | '20rem';
};

const SidebarContent = ({
  extraContent,
  footerRef,
  headerRef,
  isMobile,
  isFullSidebarVisible,
  items,
  footer,
  platformShell,
  selectedPage,
  title,
  titleRef,
}: PageLayoutSidebarProps & {
  isFullSidebarVisible: boolean;
  footerRef: RefObject<HTMLDivElement>;
  headerRef: RefObject<HTMLDivElement>;
  titleRef: RefObject<HTMLDivElement>;
}) => {
  const headerProps: BoxProps =
    !isFullSidebarVisible || isMobile
      ? {}
      : {
          position: 'sticky' as const,
          // 16 below the stuck row, the same gap the list has at rest -- which comes from the sidebar
          // root's own 16, and that padding scrolls away once the list sticks.
          top: `${platformShell.headerHeight + PAGE_HEADER_STICKY_HEIGHT + 16}px`,
        };

  const footerProps: BoxProps =
    !isFullSidebarVisible || isMobile
      ? {}
      : {
          position: 'sticky' as const,
          bottom: 0,
        };

  return (
    <>
      {title && <BitkitPageSidebar.Title ref={titleRef} {...title} />}
      {/* Only the list goes in `Body`: its `marginBlockEnd` is the gap before a *sibling* footer,
          so with the footer nested inside it the margin landed under the footer instead -- 16px of
          empty space at the very bottom of the sidebar. No top padding of its own either, the
          sidebar root already brings 16 from the recipe. */}
      <BitkitPageSidebar.Body>
        <Box ref={headerRef} {...headerProps}>
          {items.map((item, index) => {
            if ('divider' in item) {
              if (item.label) {
                return <BitkitPageSidebar.GroupHeading key={item.label} label={item.label} />;
              }
              // eslint-disable-next-line react/no-array-index-key
              return <BitkitPageSidebar.Divider key={index} />;
            }

            if ('custom' in item) {
              return <Fragment key={item.key}>{item.custom}</Fragment>;
            }

            const isSelected = selectedPage === item.pageName && !isMobile;

            // `disabled` is pulled out too: `BitkitPageSidebar.Item` disables through `state`, so this
            // would fall through the rest spread and land on the anchor as `<a disabled="">`.
            const { disabled, disabledTooltip, ...itemProps } = item.itemProps;

            return (
              // The icon's colour is the item slot's own -- it already switches to
              // `icon/interactive` when selected and `icon/disabled` when disabled.
              <BitkitPageSidebar.Item
                key={item.pageName}
                {...itemProps}
                badge={
                  item.badge && (
                    <BitkitBadge colorVariant={item.badge.colorVariant} variant={item.badge.variant}>
                      {item.badge.label}
                    </BitkitBadge>
                  )
                }
                disabledReason={disabledTooltip}
                icon={item.icon}
                selected={isSelected}
                state={disabled ? 'disabled' : undefined}
                suffixIcon={item.suffixIcon}
              >
                {item.label}
              </BitkitPageSidebar.Item>
            );
          })}
          {extraContent && <>{extraContent}</>}
        </Box>
      </BitkitPageSidebar.Body>
      {footer && (
        // No padding of its own: this wrapper exists to give the block a background and to stick it
        // to the bottom, and the spacing is the callers' `BitkitPageSidebar.Footer` -- its
        // `marginBlockStart` is the other half of `Body`'s `marginBlockEnd` above the rule, and its
        // `paddingBlock` is the 8 below the rule and the 8 the sidebar ends on. Padding here was
        // added on top of all three, so the rule sat 32 under the list and the sidebar ended on 16.
        <Box backgroundColor="background/primary" {...footerProps} ref={footerRef}>
          {footer}
        </Box>
      )}
    </>
  );
};

/**
 * When full sidebar is visible the header and footer parts of the sidebar are sticky separately
 * (header is sticky to the top, footer is to the bottom).
 * When full sidebar is not visible, sidebar has an internal scrollbar and the header and footer
 * parts of the sidebar are not separated but sticky to the top as one unit.
 */
const PageLayoutSidebar = (props: PageLayoutSidebarProps) => {
  const { clarityUnmask, isMobile, isStickyHeader, platformShell, pageHeaderRef, width } = props;

  const sidebarRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  const { isFullSidebarVisible } = useIsFullSidebarVisible({
    isStickyHeader,
    platformShellHeaderHeight: platformShell.headerHeight,
    footerRef,
    headerRef,
    sidebarRef,
    pageHeaderRef,
    titleRef,
  });

  const { sidebarHeight } = useSidebarHeight({
    isStickyHeader,
    pageHeaderRef,
    sidebarRef,
  });

  const unMaskSidebar = clarityUnmask !== false;

  return (
    <BitkitPageSidebar
      ref={sidebarRef}
      data-testid="page-navigation-sidebar"
      data-clarity-unmask={unMaskSidebar}
      width={isMobile ? '100%' : width}
      insetInlineStart={!isMobile ? platformShell.navWidth : '0'}
      // The layout sizes this sidebar itself, so the component's own `100dvh` floor has to give way.
      minHeight="auto"
      // Style props rather than `css`, which is the plainer way to say it now that the root merges
      // a consumer `css` with its own slot. `.Item` still replaces it, so the distinction is worth
      // keeping in mind one level down.
      {...(isFullSidebarVisible || isMobile
        ? {}
        : {
            height: sidebarHeight,
            overflowY: 'auto' as const,
            position: 'sticky' as const,
            top: `${platformShell.headerHeight + PAGE_HEADER_STICKY_HEIGHT}px`,
            zIndex: 100,
          })}
    >
      <SidebarContent
        {...props}
        footerRef={footerRef}
        headerRef={headerRef}
        isFullSidebarVisible={isFullSidebarVisible}
        titleRef={titleRef}
      />
    </BitkitPageSidebar>
  );
};

export default PageLayoutSidebar;
